import {
  Sparkles,
  Heart,
  Calendar,
  Bot,
  User,
  Activity,
  FileText,
  Pill,
  FolderOpen,
  CalendarPlus,
  MessageSquare,
  Settings,
  Smartphone,
  HelpCircle,
  Route,
  Shield,
  Lock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/** A primary tab in the icon rail / bottom tab bar */
export interface PrimaryTab {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string | null;
  isCompanion?: boolean;
}

/** A sub-navigation item within a section */
export interface SubNavItem {
  id: string;
  label: string;
  path: string;
  icon?: LucideIcon;
}

/** Primary tabs for icon rail (desktop) and bottom tab bar (mobile) */
export const primaryTabs: PrimaryTab[] = [
  { id: 'home', label: 'Home', icon: Sparkles, path: '/' },
  { id: 'health', label: 'Health', icon: Heart, path: '/health' },
  { id: 'care', label: 'Care', icon: Calendar, path: '/care' },
  { id: 'companion', label: 'Companion', icon: Bot, path: null, isCompanion: true },
  { id: 'me', label: 'Me', icon: User, path: '/me' },
];

/** Sub-navigation items for each primary section */
export const subNavigation: Record<string, SubNavItem[]> = {
  health: [
    { id: 'vitals', label: 'Vitals', path: '/health', icon: Activity },
    { id: 'records', label: 'Records', path: '/medical-history', icon: FileText },
    { id: 'tests', label: 'Tests', path: '/test-results', icon: Activity },
    { id: 'medications', label: 'Medications', path: '/medications', icon: Pill },
    { id: 'documents', label: 'Documents', path: '/medical-records', icon: FolderOpen },
  ],
  care: [
    { id: 'upcoming', label: 'Upcoming', path: '/appointments', icon: Calendar },
    { id: 'schedule', label: 'Schedule', path: '/schedule-appointment', icon: CalendarPlus },
    { id: 'care-pathways', label: 'Pathways', path: '/care-pathways', icon: Route },
    { id: 'messages', label: 'Messages', path: '/messages', icon: MessageSquare },
  ],
  me: [
    { id: 'profile', label: 'Profile', path: '/profile', icon: User },
    { id: 'settings', label: 'Settings', path: '/settings', icon: Settings },
    { id: 'devices', label: 'Devices', path: '/health-hub', icon: Smartphone },
    { id: 'consent', label: 'Consent', path: '/consent', icon: Shield },
    { id: 'vault', label: 'Vault', path: '/vault', icon: Lock },
    { id: 'help', label: 'Help', path: '/help', icon: HelpCircle },
  ],
};

/** Determine which primary section a given path belongs to */
export function getActivePrimaryTab(pathname: string): string {
  if (pathname === '/') return 'home';
  if (
    pathname.startsWith('/health') ||
    pathname.startsWith('/medical-history') ||
    pathname.startsWith('/test-results') ||
    pathname.startsWith('/medications') ||
    pathname.startsWith('/medical-records')
  )
    return 'health';
  if (
    pathname.startsWith('/appointments') ||
    pathname.startsWith('/schedule-appointment') ||
    pathname.startsWith('/care-pathways') ||
    pathname.startsWith('/messages')
  )
    return 'care';
  if (
    pathname.startsWith('/profile') ||
    pathname.startsWith('/settings') ||
    pathname.startsWith('/help') ||
    pathname.startsWith('/consent') ||
    pathname.startsWith('/vault') ||
    pathname.startsWith('/me')
  )
    return 'me';
  if (pathname.startsWith('/ai-health-companion') || pathname.startsWith('/ai-assistant'))
    return 'companion';
  return 'home';
}

/** Get the sub-navigation items for the active section */
export function getSubNavItems(pathname: string): SubNavItem[] | null {
  const tab = getActivePrimaryTab(pathname);
  return subNavigation[tab] || null;
}
